'use client'

import { IProductVariant } from '@/lib/types/core/product-variant'
import { resolvePrice, type PriceTier } from '@/lib/utils/price-resolution'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { isWidthAxis } from './color-swatch-rail'

// --- Types ---

export interface VariantInventoryStatus {
  variantInventory: Array<{
    variantId: string
    inventory: number
    inStock: boolean
  }>
}

export interface VariantSelection {
  variantId: string | null
  mode: 'cut' | 'roll'
  quantity: number
}

// --- Price calculator ---

export function calculateVariantPrice(
  variant: Pick<IProductVariant, 'pricePerUnit' | 'rollPrice' | 'retailPrice' | 'wholesalePrice'>,
  mode: 'cut' | 'roll',
  quantity: number,
  tier: PriceTier = 'RETAIL'
): number {
  if (mode === 'roll') {
    // No wholesale roll price exists on ProductVariant — roll pricing is
    // tier-agnostic, matching the server's resolveLineUnitPrice.
    return Number(variant.rollPrice ?? 0) * quantity
  }
  const tierPrice = resolvePrice(
    {
      retailPrice: variant.retailPrice ?? variant.pricePerUnit ?? null,
      wholesalePrice: variant.wholesalePrice ?? null,
      rollPrice: variant.rollPrice ?? null,
      rollLengthUnits: null
    },
    tier
  )
  return Number(tierPrice ?? 0) * quantity
}

export function useVariantSelection(
  variants: IProductVariant[],
  inventoryStatus: VariantInventoryStatus,
  tier: PriceTier = 'RETAIL'
) {
  const [selection, setSelection] = useState<VariantSelection>({
    variantId: null,
    mode: 'cut',
    quantity: 0
  })

  const selectedVariant = useMemo(() => {
    if (!selection.variantId) return null
    return variants.find(v => v.id === selection.variantId) ?? null
  }, [selection.variantId, variants])

  // Reset stale variant ID
  useEffect(() => {
    if (selection.variantId && !variants.find(v => v.id === selection.variantId)) {
      setSelection({ variantId: null, mode: 'cut', quantity: 0 })
    }
  }, [selection.variantId, variants])

  const canShowRollMode = useMemo(() => {
    if (!selectedVariant) return false
    return !!(selectedVariant.rollPrice && selectedVariant.rollLengthUnits)
  }, [selectedVariant])

  const variantStock = useMemo(() => {
    if (!selection.variantId) return null
    return inventoryStatus.variantInventory.find(v => v.variantId === selection.variantId) ?? null
  }, [selection.variantId, inventoryStatus.variantInventory])

  const availableInventory = variantStock?.inventory ?? 0

  const minQty = useMemo(() => {
    if (selection.mode === 'roll') return 1
    return Number(selectedVariant?.minOrderQuantity ?? 1)
  }, [selection.mode, selectedVariant])

  const maxQty = useMemo(() => {
    if (selection.mode === 'roll') {
      const rollLength = Number(selectedVariant?.rollLengthUnits ?? 1)
      return Math.max(1, Math.floor(availableInventory / rollLength))
    }
    const maxOrder = Number(selectedVariant?.maxOrderQuantity ?? Infinity)
    return Math.min(maxOrder, availableInventory)
  }, [selection.mode, selectedVariant, availableInventory])

  const stepSize = useMemo(() => {
    if (selection.mode === 'roll') return 1
    return Number(selectedVariant?.incrementStep ?? 1)
  }, [selection.mode, selectedVariant])

  const derivedPrice = useMemo(() => {
    if (!selectedVariant) return 0
    return calculateVariantPrice(selectedVariant, selection.mode, selection.quantity, tier)
  }, [selectedVariant, selection.mode, selection.quantity, tier])

  // The price-per-unit line used to read variant.pricePerUnit directly, which
  // is only the legacy attributeValues fallback: on variants carrying the real
  // retailPrice/wholesalePrice columns it is null, so the page showed
  // "$0.00 x 1 = $129.25". Ask the same function that produces the total, for a
  // quantity of one, and the two halves of that line always agree.
  const derivedUnitPrice = useMemo(() => {
    if (!selectedVariant) return 0
    return calculateVariantPrice(selectedVariant, selection.mode, 1, tier)
  }, [selectedVariant, selection.mode, tier])

  const isValid = useMemo(() => {
    if (!selectedVariant) return false
    if (!variantStock?.inStock) return false
    if (selection.quantity < minQty || selection.quantity > maxQty) return false
    return true
  }, [selectedVariant, variantStock, selection.quantity, minQty, maxQty])

  const validationError = useMemo((): string | null => {
    if (!selectedVariant) return isWidthAxis(variants) ? 'Select a width' : 'Select a color'
    if (!variantStock?.inStock) return 'Out of stock'
    if (selection.quantity < minQty) return 'Quantity too low'
    if (selection.quantity > maxQty) return 'Quantity exceeds stock'
    return null
  }, [selectedVariant, variantStock, selection.quantity, minQty, maxQty, variants])

  const setColor = useCallback((variantId: string) => {
    const variant = variants.find(v => v.id === variantId)
    if (!variant) return
    const canRoll = !!(variant.rollPrice && variant.rollLengthUnits)
    const newMode = (selection.mode === 'roll' && !canRoll) ? 'cut' : selection.mode
    const newQty = newMode === 'roll' ? 1 : Number(variant.minOrderQuantity ?? 1)
    setSelection({ variantId, mode: newMode, quantity: newQty })
  }, [variants, selection.mode])

  const setMode = useCallback((mode: 'cut' | 'roll') => {
    if (mode === 'roll' && !canShowRollMode) return
    const newQty = mode === 'roll' ? 1 : Number(selectedVariant?.minOrderQuantity ?? 1)
    setSelection(prev => ({ ...prev, mode, quantity: newQty }))
  }, [canShowRollMode, selectedVariant])

  const setQuantity = useCallback((qty: number) => {
    const clamped = Math.max(minQty, Math.min(maxQty, qty))
    const snapped = Math.round(clamped / stepSize) * stepSize
    setSelection(prev => ({ ...prev, quantity: snapped }))
  }, [minQty, maxQty, stepSize])

  return {
    selection,
    setColor,
    setMode,
    setQuantity,
    selectedVariant,
    derivedPrice,
    derivedUnitPrice,
    isValid,
    validationError,
    canShowRollMode,
    availableInventory,
    minQty,
    maxQty,
    stepSize
  }
}

/** Return shape of useVariantSelection, for components that receive it as a prop rather than calling the hook themselves. */
export type UseVariantSelectionResult = ReturnType<typeof useVariantSelection>
